import { observer } from 'mobx-react-lite'
import { Outlet, useLoaderData, useOutletContext } from 'react-router'
import { ContentError } from '@/components/ContentError'
import { ContentLoading } from '@/components/ContentLoading'
import { useActivation } from '@/hooks/view-model-hooks'
import { isAbortError } from '@/utils/abort-util'
import type { CallCenterJobPageLoaderData } from '@/features/call-center/call-center-route-loaders'
import { type CallCenterViewModel } from '@/features/call-center/CallCenterViewModel'
import { type CallCenterJobPageViewModel } from '@/features/call-center/CallCenterJobPageViewModel'
import { JobNotSupportedError } from '@/features/call-center/job-not-supported-error'
import { JobNotSupportedContainer } from '@/features/call-center/components/JobNotSupported/JobNotSupportedContainer'
import { CallCenterFrameContainer } from './CallCenterFrameContainer'

export default observer(function CallCenterJobPageContainer() {
  const vm = useOutletContext<CallCenterViewModel>()
  const data = useLoaderData<CallCenterJobPageLoaderData>()
  return data ? (
    <CallCenterJobPageViewContainer vm={vm} jobPageVm={data.vm} />
  ) : null
})

interface CallCenterJobPageViewContainerProps {
  vm: CallCenterViewModel
  jobPageVm: CallCenterJobPageViewModel
}

const CallCenterJobPageViewContainer = observer(
  function CallCenterJobPageViewContainer({
    vm,
    jobPageVm,
  }: CallCenterJobPageViewContainerProps) {
    useActivation(jobPageVm)

    return jobPageVm.fetch.match({
      pending: () => <ContentLoading label="Loading job..." />,
      rejected: (err) => {
        // A load that was aborted for another job is about to be replaced.
        if (isAbortError(err)) {
          return <ContentLoading label="Loading job..." />
        }

        return err instanceof JobNotSupportedError ? (
          <JobNotSupportedContainer />
        ) : (
          <ContentError error={err} />
        )
      },
      resolved: () => (
        <CallCenterFrameContainer vm={vm}>
          <Outlet context={vm} />
        </CallCenterFrameContainer>
      ),
    })
  },
)
